import type {
  Currency,
  DueEntry,
  FxRates,
  MoneySummary,
  SubPayment,
  Subscription,
} from "../shared/money";

/**
 * Schedule math. Everything here is pure and date-string based (YYYY-MM-DD in
 * Kyiv terms) — no Date arithmetic across timezones, so a charge on the 31st
 * behaves the same wherever the Worker runs.
 */

export const RATE_SCALE = 10_000;

export function todayKyiv(now = new Date()): string {
  // Kyiv is UTC+2/+3; en-CA gives YYYY-MM-DD.
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Kyiv" }).format(now);
}

function parts(day: string): [number, number, number] {
  const [y, m, d] = day.split("-").map(Number);
  return [y ?? 1970, m ?? 1, d ?? 1];
}

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function fmt(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * Add `n` months to an ISO day, clamping the day-of-month (Jan 31 + 1mo →
 * Feb 28/29) while keeping the original anchor day for later steps.
 */
export function addMonths(day: string, n: number): string {
  const [y, m, d] = parts(day);
  const total = (y * 12 + (m - 1) + n);
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  return fmt(year, month, Math.min(d, daysInMonth(year, month)));
}

export function addYears(day: string, n: number): string {
  const [y, m, d] = parts(day);
  return fmt(y + n, m, Math.min(d, daysInMonth(y + n, m)));
}

export function daysBetween(from: string, to: string): number {
  const [fy, fm, fd] = parts(from);
  const [ty, tm, td] = parts(to);
  return Math.round(
    (Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000,
  );
}

/** Step `index` of a subscription's schedule (0-based). */
export function occurrence(sub: Subscription, index: number): string {
  if (sub.kind === "installment" || sub.cycle === "monthly") {
    return addMonths(sub.firstDate, index);
  }
  return addYears(sub.firstDate, index);
}

/** Total number of charges, or null for an open-ended recurring sub. */
export function totalOccurrences(sub: Subscription): number | null {
  return sub.kind === "installment" ? (sub.parts ?? 0) : null;
}

/**
 * Every charge that is settled (paid or skipped) up to and including `index`
 * is filtered out; the first unsettled charge is what the UI shows as due.
 * Returns the first `limit` unsettled charges not later than `horizon`.
 */
export function pendingOccurrences(
  sub: Subscription,
  settled: Set<string>,
  today: string,
  horizon: string,
  limit = 3,
): Array<{ dueDate: string; index: number }> {
  const out: Array<{ dueDate: string; index: number }> = [];
  const total = totalOccurrences(sub);
  const cap = total ?? 600; // ~50 years of monthly charges
  for (let i = 0; i < cap && out.length < limit; i++) {
    const dueDate = occurrence(sub, i);
    if (settled.has(dueDate)) continue;
    // Unsettled charges in the past stay visible (that is the whole point).
    if (dueDate > horizon && dueDate > today) break;
    out.push({ dueDate, index: i });
  }
  return out;
}

export function toUah(amount: number, currency: Currency, fx: FxRates): number {
  if (currency === "UAH") return amount;
  const rate = currency === "USD" ? fx.mono.USD : fx.mono.EUR;
  return Math.round(amount * rate);
}

export function rateFor(currency: Currency, fx: FxRates): number {
  if (currency === "UAH") return 1;
  return currency === "USD" ? fx.mono.USD : fx.mono.EUR;
}

export interface DueOptions {
  today: string;
  /** Days ahead to include (default 14). */
  horizonDays?: number;
}

export function buildDue(
  subs: Subscription[],
  payments: SubPayment[],
  fx: FxRates,
  { today, horizonDays = 14 }: DueOptions,
): DueEntry[] {
  const horizon = addDays(today, horizonDays);
  const settledBySub = new Map<string, Set<string>>();
  for (const p of payments) {
    const set = settledBySub.get(p.subId) ?? new Set<string>();
    set.add(p.dueDate);
    settledBySub.set(p.subId, set);
  }

  const out: DueEntry[] = [];
  for (const sub of subs) {
    if (sub.cancelledAt !== null) continue;
    if (sub.kind === "recurring" && sub.paused) continue;
    const settled = settledBySub.get(sub.id) ?? new Set<string>();
    for (const { dueDate, index } of pendingOccurrences(sub, settled, today, horizon)) {
      out.push({
        subId: sub.id,
        name: sub.name,
        kind: sub.kind,
        dueDate,
        amount: sub.amount,
        currency: sub.currency,
        amountUah: toUah(sub.amount, sub.currency, fx),
        daysLeft: daysBetween(today, dueDate),
        partNumber: sub.kind === "installment" ? index + 1 : null,
        parts: sub.parts,
      });
    }
  }
  return out.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
}

export function addDays(day: string, n: number): string {
  const [y, m, d] = parts(day);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return fmt(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

export function buildSummary(
  subs: Subscription[],
  payments: SubPayment[],
  due: DueEntry[],
  fx: FxRates,
  today: string,
): MoneySummary {
  let monthlyUah = 0;
  let installmentsLeftUah = 0;
  const paidCount = new Map<string, number>();
  for (const p of payments) {
    paidCount.set(p.subId, (paidCount.get(p.subId) ?? 0) + 1);
  }

  for (const sub of subs) {
    if (sub.cancelledAt !== null) continue;
    const uah = toUah(sub.amount, sub.currency, fx);
    if (sub.kind === "recurring") {
      if (sub.paused) continue;
      monthlyUah += sub.cycle === "yearly" ? Math.round(uah / 12) : uah;
    } else {
      const left = Math.max(0, (sub.parts ?? 0) - (paidCount.get(sub.id) ?? 0));
      installmentsLeftUah += left * uah;
    }
  }

  const monthEnd = `${today.slice(0, 7)}-${String(
    daysInMonth(Number(today.slice(0, 4)), Number(today.slice(5, 7))),
  ).padStart(2, "0")}`;
  const restOfMonthUah = due
    .filter((d) => d.dueDate <= monthEnd)
    .reduce((sum, d) => sum + d.amountUah, 0);

  return {
    monthlyUah,
    restOfMonthUah,
    installmentsLeftUah,
    dueSoonCount: due.length,
    overdueCount: due.filter((d) => d.daysLeft < 0).length,
  };
}
